import { MODULE_NAME } from './const';
import StoreModuleService from '../StoreModuleService';
import { EdgeTypes } from '@/components/FlowTree/flow-tree-service.const';
import { getAllActions, getAction } from '@/store/modules/actions/getters';
import { useWorkflowVersionGraph } from '@/store/modules/treeBlocks/getters';
import { rootNodeId as rootNodeIdGetter } from '@/store/modules/workflows/getters';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { arrayToObject } from '@/utils/helpers';
import * as WorkflowGraphService from '@/utils/workflow/workflow-graph-service';

const buildWorkflowGraphsGetterObject = (getter) => StoreModuleService.buildGetterObject(MODULE_NAME, getter);

// getters declarations
export const isSelectingWorkflowRoute = buildWorkflowGraphsGetterObject('isSelectingWorkflowRoute');
export const isRouteSelected = buildWorkflowGraphsGetterObject('isRouteSelected');
export const rootNodeOfRouteSelection = buildWorkflowGraphsGetterObject('rootNodeOfRouteSelection');
export const lastNodeOfRouteSelection = buildWorkflowGraphsGetterObject('lastNodeOfRouteSelection');
export const isInSelectedWorkflowRoute = buildWorkflowGraphsGetterObject('isInSelectedWorkflowRoute');
export const nodesInSelectedRoute = buildWorkflowGraphsGetterObject('nodesInSelectedRoute');
export const getNodesInSelectedRoute = buildWorkflowGraphsGetterObject('getNodesInSelectedRoute');
export const isValidRouteEnd = buildWorkflowGraphsGetterObject('isValidRouteEnd');
export const useNodeAncestorIds = buildWorkflowGraphsGetterObject('useNodeAncestorIds');
export const useBranchConsolidatePathAncestorIds = buildWorkflowGraphsGetterObject('useBranchConsolidatePathAncestorIds');
export const useNodePathsFromRoot = buildWorkflowGraphsGetterObject('useNodePathsFromRoot');
export const useIsNodeInBranch = buildWorkflowGraphsGetterObject('useIsNodeInBranch');
export const useIsNodeInLoop = buildWorkflowGraphsGetterObject('useIsNodeInLoop');
export const useIsNodeCompound = buildWorkflowGraphsGetterObject('useIsNodeCompound');

const getters = {
	[isSelectingWorkflowRoute.getter]: (state) => state.isSelectingWorkflowRoute,
	[isRouteSelected.getter]: (state) => state.isRouteSelected,
	[rootNodeOfRouteSelection.getter]: (state) => state.rootNodeOfRouteSelection,
	[lastNodeOfRouteSelection.getter]: (state) => state.lastNodeOfRouteSelection,
	[nodesInSelectedRoute.getter]: (state, getters) => {
		const { rootNodeOfRouteSelection, lastNodeOfRouteSelection } = state;
		const nodesInClusteredRoute = getters[getNodesInSelectedRoute.getter](rootNodeOfRouteSelection, lastNodeOfRouteSelection);
		return nodesInClusteredRoute;
	},
	[isInSelectedWorkflowRoute.getter]: (state, getters) => (nodeId) => {
		const nodesInClusteredRoute = getters[nodesInSelectedRoute.getter];
		const result = nodesInClusteredRoute.includes(nodeId) || nodeId === state.rootNodeOfRouteSelection;
		return result;
	},
	[getNodesInSelectedRoute.getter]: (state, getters, rootState, rootGetters) => (firstNode, lastNode) => {
		const actionsMap = rootGetters[getAllActions.fullPath];
		return lastNode && firstNode && state.workflowGraphs
			? WorkflowGraphService.nodesInRoute(state.workflowGraphs, String(firstNode), String(lastNode), actionsMap)
			: [];
	},
	[useNodeAncestorIds.getter]:
		(state, getters, rootState, rootGetters) =>
		({ node }) => {
			const nodeId = node.id;
			const workflowId = node.workflow;
			const workflowVersionId = node.workflow_version;
			const rootNodeId = rootGetters[rootNodeIdGetter.fullPath](workflowId, workflowVersionId);
			const { nodes, edges } = rootGetters[useWorkflowVersionGraph.fullPath](workflowId, workflowVersionId);
			const edgesMap = arrayToObject(edges);
			const wg = WorkflowGraphService.createWorkflowGraph(nodes, edges, rootGetters[getAction.fullPath]);
			const ancestorsIds = WorkflowGraphService.getAncestors(wg, String(rootNodeId), String(nodeId), edgesMap);
			return ancestorsIds.filter((id) => String(id) !== String(nodeId)).toReversed();
		},
	[useBranchConsolidatePathAncestorIds.getter]:
		(state, getters, rootState, rootGetters) =>
		({ node, pathId }) => {
			const workflowId = node.workflow;
			const workflowVersionId = node.workflow_version;
			const { nodes, edges } = rootGetters[useWorkflowVersionGraph.fullPath](workflowId, workflowVersionId);
			const edgesMap = arrayToObject(edges);
			const pathEdge = edgesMap[pathId];
			const pathRootNodeId = pathEdge.target;
			if (pathRootNodeId === node.id) {
				// no nodes in path to consolidate
				return [];
			}
			const actionsMap = rootGetters[getAllActions.fullPath];
			const wg = WorkflowGraphService.createWorkflowGraph(nodes, edges, rootGetters[getAction.fullPath]);
			const allPathsBetweenPathRootToConsolidateNode = WorkflowGraphService.getAllSimplePaths(wg, pathRootNodeId, node.id);
			const shortestPath = allPathsBetweenPathRootToConsolidateNode.reduce((acc, path) => {
				if (path.length < acc.length) {
					return path;
				}
				return acc;
			}, allPathsBetweenPathRootToConsolidateNode[0]);
			const shortestPathWithoutBranchRoots = shortestPath.filter(
				(node) => !WorkflowNodeService.isBranchRootNode(node, actionsMap[node.action])
			);
			return shortestPathWithoutBranchRoots.filter(({ id }) => id !== node.id).map(({ id }) => String(id));
		},
	[useNodePathsFromRoot.getter]:
		(state, getters, rootState, rootGetters) =>
		({ node }) => {
			const nodeId = node.id;
			const workflowId = node.workflow;
			const workflowVersionId = node.workflow_version;
			const rootNodeId = rootGetters[rootNodeIdGetter.fullPath](workflowId, workflowVersionId);
			const { nodes, edges } = rootGetters[useWorkflowVersionGraph.fullPath](workflowId, workflowVersionId);
			const visibleEdges = edges.filter((edge) => [EdgeTypes.Next, EdgeTypes.Diverge].includes(edge.type));
			const wg = WorkflowGraphService.createWorkflowGraph(nodes, visibleEdges, rootGetters[getAction.fullPath]);
			return WorkflowGraphService.getAllSimplePaths(wg, rootNodeId, nodeId).map((path) =>
				path.filter(({ id }) => String(id) !== String(nodeId))
			);
		},
	[useIsNodeInBranch.getter]:
		(state, getters, rootState, rootGetters) =>
		({ node, nodes }) => {
			const workflowId = node.workflow;
			const workflowVersionId = node.workflow_version;
			const { nodes: nodesFromStore, edges } = rootGetters[useWorkflowVersionGraph.fullPath](workflowId, workflowVersionId);
			const relevantNodes = nodes || nodesFromStore;
			const rootNodeId = rootGetters[rootNodeIdGetter.fullPath](workflowId, workflowVersionId);
			const nodesMap = arrayToObject(relevantNodes);
			const wg = WorkflowGraphService.createWorkflowGraph(nodes, edges, rootGetters[getAction.fullPath]);
			const actionsMap = rootGetters[getAllActions.fullPath];
			if (WorkflowNodeService.isBranchCompoundNode(node, actionsMap[node.action])) {
				return false;
			}
			return WorkflowGraphService.isInBranch(wg, rootNodeId, nodesMap, node, actionsMap);
		},
	[useIsNodeInLoop.getter]:
		(state, getters, rootState, rootGetters) =>
		({ node, nodes }) => {
			const workflowId = node.workflow;
			const workflowVersionId = node.workflow_version;
			const { nodes: nodesFromStore, edges } = rootGetters[useWorkflowVersionGraph.fullPath](workflowId, workflowVersionId);
			const relevantNodes = nodes || nodesFromStore;
			const rootNodeId = rootGetters[rootNodeIdGetter.fullPath](workflowId, workflowVersionId);
			const nodesMap = arrayToObject(relevantNodes);
			const wg = WorkflowGraphService.createWorkflowGraph(nodes, edges, rootGetters[getAction.fullPath]);
			const actionsMap = rootGetters[getAllActions.fullPath];
			if (WorkflowNodeService.isLoopCompoundNode(node, actionsMap[node.action])) {
				return false;
			}
			return WorkflowGraphService.isInLoop(wg, rootNodeId, nodesMap, node, actionsMap);
		},
	[useIsNodeCompound.getter]:
		(state, getters, rootState, rootGetters) =>
		({ node }) =>
			WorkflowNodeService.isCompoundNode(node, rootGetters[getAllActions.fullPath][node.action]),

	[isValidRouteEnd.getter]: (state, getters, rootState, rootGetters) => (nodeId) =>
		WorkflowGraphService.isValidRouteEnd(
			state.workflowGraphs,
			String(state.rootNodeOfRouteSelection),
			String(nodeId),
			rootGetters[getAllActions.fullPath]
		),
};

export default getters;
